import { schemaValidator } from 'schemas';

// Define DataContainer interface locally if not imported, for type safety
interface DataContainer {
  query(queryObj: any): Promise<any>;
  // Add other relevant DataContainer properties/methods if needed for type checking
}

// --- Message Interfaces ---
interface WorkerInitMessage {
  type: 'init';
  userCode: string;
  moduleMap: Record<string, string>;
}

interface WorkerExecuteQueryMessage {
  type: 'query';
  queryId: string;
  inputData: Record<string, any>; // Resolved data from DataContainers, keyed by local input name (sourceId)
  queryObj: any; // The original query object passed to Transform.query
}

type ToWorkerMessage = WorkerInitMessage | WorkerExecuteQueryMessage;

interface WorkerReadyResponseMessage {
  type: 'ready';
}

interface WorkerInitErrorResponseMessage {
  type: 'init_error';
  error: string;
}

interface WorkerQueryResponseMessage {
  type: 'query_response';
  queryId: string;
  result?: any;
  error?: string;
  details?: any;
  stack?: string;
}

type FromWorkerMessage =
  | WorkerReadyResponseMessage
  | WorkerInitErrorResponseMessage
  | WorkerQueryResponseMessage;

// --- Pending Query Interface ---
interface PendingQuery {
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
  timer: number;
}

// --- TransformSpecification for init method ---
export interface InitTransformSpec {
  id: string;
  code: string;
  inputs: string[]; // Array of source IDs, which will also be the keys in the 'inputs' object for the transform function
  libraries?: Record<string, string>;
  outputType?: string;
}

export class Transform {
  private id: string;
  private code: string;
  private modules: Record<string, string>;
  private outputType: string;
  private resolvedInputsMap: Record<string, DataContainer>; // Map of sourceID/localName to DataContainer

  private worker: Worker | null = null;
  private workerUrl: string | null = null;
  private pendingQueries: Map<string, PendingQuery> = new Map();
  private nextQueryId = 0;
  private readonly QUERY_TIMEOUT_MS = 30000;
  private workerReady: boolean = false;

  private constructor(
    spec: InitTransformSpec,
    resolvedInputsMap: DataContainer[],
  ) {
    this.id = spec.id;
    this.code = spec.code;
    this.modules = spec.modules || {};
    this.outputType = spec.outputType || 'dataset';
    this.resolvedInputsMap = resolvedInputsMap;

    const workerScript = `
      let transformFn = null;
      let importedModules = null;
      let initErrorMsg = null;

      self.onmessage = async (event) => {
        const msg = event.data;

        if (msg.type === 'init') {
          try {
            importedModules = {};
            for (const alias in msg.moduleMap) {
              try {
                importedModules[alias] = await import(msg.moduleMap[alias]);
              } catch (e) {
                console.error('Worker: Error importing module ' + alias + ' (' + msg.moduleMap[alias] + '):', e);
                throw new Error('Module import failed for ' + alias + ': ' + e.message);
              }
            }
            const fnArgs=['$inputs','$query','$outputType',...Object.keys(importedModules)];
            transformFn = new Function(...fnArgs, msg.userCode);
            self.postMessage({ type: 'ready' });
          } catch (e) {
            initErrorMsg = e.message;
            console.error('Worker: Initialization failed:', e);
            self.postMessage({ type: 'init_error', error: initErrorMsg });
          }
          return;
        }

        if (msg.type === 'query') {
          if (initErrorMsg) {
            self.postMessage({ type: 'query_response', queryId: msg.queryId, error: 'Worker initialization failed: ' + initErrorMsg });
            return;
          }
          if (!transformFn || !importedModules) {
            self.postMessage({ type: 'query_response', queryId: msg.queryId, error: 'Worker not ready or initialization incomplete.' });
            return;
          }
          try {
            const result = await transformFn(msg.inputData, msg.queryObj,msg.outputType, ...Object.values(importedModules));
            self.postMessage({ type: 'query_response', queryId: msg.queryId, result });
          } catch (e) {
            console.error('Worker: Error executing user code for query ' + msg.queryId + ':', e);
            self.postMessage({ type: 'query_response', queryId: msg.queryId, error: e.message, stack: e.stack });
          }
          return;
        }
      };
    `;

    try {
      const blob = new Blob([workerScript], { type: 'application/javascript' });
      this.workerUrl = URL.createObjectURL(blob);
      this.worker = new Worker(this.workerUrl);
      // Temporary handlers for init phase are set up in the static init method's Promise
    } catch (e) {
      console.error(`Failed to create worker for transform '${this.id}':`, e);
      if (this.workerUrl) URL.revokeObjectURL(this.workerUrl);
      this.worker = null;
      this.workerUrl = null;
      // This error will be caught by the Promise in the static init method
      throw e;
    }
  }

  public static init(
    spec: InitTransformSpec,
    inputDataContainers: DataContainer[],
  ): Promise<Transform | null> {
    if (!spec.code) {
      console.error(`Transform '${spec.id}' declared without code.`);
      return Promise.resolve(null);
    }

    if (spec.inputs.length !== inputDataContainers.length) {
      console.error(
        `Transform '${spec.id}': Mismatch between specified inputs (${spec.inputs.length}) and provided data containers (${inputDataContainers.length}).`,
      );
      return Promise.resolve(null);
    }

    let transformInstance: Transform;
    try {
      transformInstance = new Transform(spec, inputDataContainers);
    } catch (e) {
      console.error(`Transform '${spec.id}' constructor failed:`, e);
      return Promise.resolve(null);
    }

    return new Promise<Transform | null>((resolve) => {
      if (!transformInstance.worker) {
        // Should have been caught by constructor throw
        resolve(null);
        return;
      }

      const tempOnError = (error: any) => {
        console.error(
          `Worker initialization error for transform '${spec.id}':`,
          error,
        );
        transformInstance.workerReady = false;
        cleanupAndResolve(null);
      };

      const tempOnMessage = (event: MessageEvent<FromWorkerMessage>) => {
        if (event.data.type === 'ready') {
          transformInstance.workerReady = true;
          console.log(
            `Transform worker for '${spec.id}' is ready (via static init).`,
          );
          cleanupAndResolve(transformInstance);
        } else if (event.data.type === 'init_error') {
          console.error(
            `Worker explicit init_error for transform '${spec.id}':`,
            (event.data as WorkerInitErrorResponseMessage).error,
          );
          transformInstance.workerReady = false;
          cleanupAndResolve(null);
        } else {
          // Should not receive query responses during init
          console.warn(
            `Unexpected message type during init for ${spec.id}: ${event.data.type}`,
          );
        }
      };

      const cleanupAndResolve = (result: Transform | null) => {
        if (transformInstance.worker) {
          transformInstance.worker.removeEventListener(
            'message',
            tempOnMessage,
          );
          transformInstance.worker.removeEventListener('error', tempOnError);
          // Assign persistent handlers
          transformInstance.worker.onmessage =
            transformInstance._handleWorkerMessage.bind(transformInstance);
          transformInstance.worker.onerror =
            transformInstance._handleWorkerError.bind(transformInstance);
        }
        if (result === null) {
          transformInstance.terminate(); // Clean up if init failed
        }
        resolve(result);
      };

      transformInstance.worker.addEventListener('message', tempOnMessage);
      transformInstance.worker.addEventListener('error', tempOnError);

      // Send the init message to the worker
      const initMsg: WorkerInitMessage = {
        type: 'init',
        userCode: transformInstance.code,
        moduleMap: transformInstance.modules,
      };
      transformInstance.worker.postMessage(initMsg);
    });
  }

  private validateResult(result: any): void {
    if (this.outputType === 'dataset') {
      const valid = schemaValidator.validate(
        'https://schemas.oceanum.io/eidos/data.json#/$defs/dataset',
        result,
      );
      if (!valid) {
        throw new Error(
          'Dataset validation failed: ' +
            schemaValidator.errorsText(schemaValidator.errors),
        );
      }
    } else if (this.outputType === 'geojson') {
      const valid = schemaValidator.validate(
        'https://schemas.oceanum.io/geojson.json#/definitions/FeatureCollection',
        result,
      );
      if (!valid) {
        throw new Error(
          'GeoJSON validation failed: ' +
            schemaValidator.errorsText(schemaValidator.errors),
        );
      }
    }
  }

  private _handleWorkerMessage(event: MessageEvent<FromWorkerMessage>): void {
    const data = event.data;
    if (data.type === 'query_response') {
      const pending = this.pendingQueries.get(data.queryId);
      if (pending) {
        clearTimeout(pending.timer);
        if (data.error) {
          console.error(
            `Error in transform worker for '${this.id}', query '${data.queryId}':`,
            data.error,
            data.details || '',
            data.stack || '',
          );
          pending.reject(new Error(data.error));
        } else {
          try {
            this.validateResult(data.result);
            pending.resolve(data.result);
          } catch (e) {
            console.error(
              `Validation error in transform '${this.id}', query '${data.queryId}':`,
              e,
            );
            pending.reject(e);
          }
        }
        this.pendingQueries.delete(data.queryId);
      }
    } else {
      console.warn(
        `Transform '${this.id}': Unexpected message type received post-init: ${data.type}`,
      );
    }
  }

  private _handleWorkerError(error: any): void {
    console.error(
      `Unhandled error in transform worker for '${this.id}' post-init:`,
      error,
    );
    this.workerReady = false;
    this.pendingQueries.forEach((pending) => {
      clearTimeout(pending.timer);
      pending.reject(
        new Error('Transform worker encountered an unrecoverable error.'),
      );
    });
    this.pendingQueries.clear();
    this.terminate();
  }

  public async query(queryObj: any, squeeze?: boolean): Promise<any> {
    if (!this.worker || !this.workerReady) {
      return Promise.reject(
        new Error(
          `Transform '${this.id}' worker is not initialized or not ready.`,
        ),
      );
    }

    const actualInputData: Record<string, any> = {};
    try {
      for (const sourceId in this.resolvedInputsMap) {
        const dataContainer = this.resolvedInputsMap[sourceId];
        actualInputData[sourceId] = await dataContainer.query(queryObj);
        if (!actualInputData[sourceId]) {
          console.warn(
            `No data returned for transform '${this.id}' query from ${sourceId}`,
          );
          return Promise.resolve(null);
        }
      }
    } catch (error) {
      console.error(
        `Error fetching input data for transform '${this.id}' query:`,
        error,
      );
      return Promise.reject(error);
    }

    const queryId = (this.nextQueryId++).toString();

    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        if (this.pendingQueries.has(queryId)) {
          this.pendingQueries.delete(queryId);
          reject(
            new Error(
              `Query '${queryId}' for transform '${this.id}' timed out after ${this.QUERY_TIMEOUT_MS / 1000}s.`,
            ),
          );
        }
      }, this.QUERY_TIMEOUT_MS);

      this.pendingQueries.set(queryId, { resolve, reject, timer });

      const message: WorkerExecuteQueryMessage = {
        type: 'query',
        queryId,
        inputData: actualInputData,
        queryObj: queryObj,
        outputType: this.outputType,
      };
      this.worker!.postMessage(message);
    });
  }

  public terminate(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    if (this.workerUrl) {
      URL.revokeObjectURL(this.workerUrl);
      this.workerUrl = null;
    }
    this.workerReady = false; // Ensure ready state is false
    this.pendingQueries.forEach((pending) => {
      clearTimeout(pending.timer);
      pending.reject(new Error(`Transform '${this.id}' is terminating.`));
    });
    this.pendingQueries.clear();
    console.log(`Transform '${this.id}' terminated.`);
  }
}
